/**
 * User Permission System
 *
 * Users can have one or more permissions:
 * - advisor: Default permission - Access to Clients and Flow instances only
 * - developer: Access to API Keys and Webhooks
 * - admin: Access to User Management
 * - designer: Access to all other settings (Flows, Risk Models, Portfolios, PDF Templates, Custom Fields, etc.)
 * - compliance: Access to compliance checks, audits, and flow instance reviews
 *
 * Users must have at least one permission (default: advisor)
 */

export const PERMISSIONS = {
  ADVISOR: "advisor",
  DEVELOPER: "developer",
  ADMIN: "admin",
  DESIGNER: "designer",
  COMPLIANCE: "compliance",
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

/**
 * Check if a user's permissions allow access to a specific resource
 */
export function hasPermission(userPermissions: Permission[], resource: string): boolean {
  // Ensure we always have at least the advisor permission
  const permissions = userPermissions.length > 0 ? userPermissions : [PERMISSIONS.ADVISOR];

  switch (resource) {
    // Developer-only resources
    case "api-keys":
    case "webhooks":
      return permissions.includes(PERMISSIONS.DEVELOPER);

    // Admin-only resources
    case "users":
      return permissions.includes(PERMISSIONS.ADMIN);

    // Designer resources
    case "flows":
    case "risk-models":
    case "portfolios":
    case "pdf-templates":
    case "custom-fields":
    case "settings":
      return permissions.includes(PERMISSIONS.DESIGNER);

    // Compliance resources
    case "compliance":
      return permissions.includes(PERMISSIONS.COMPLIANCE);

    // Resources accessible to all users (advisor and above)
    case "clients":
    case "flow-instances":
    case "documentation":
      return true;

    default:
      return false;
  }
}

/**
 * Check if user has a specific permission
 */
export function hasPermissionType(userPermissions: Permission[], permission: Permission): boolean {
  return userPermissions.includes(permission);
}

/**
 * Compliance reviewers may open flow instances but only in read-only mode,
 * unless they also hold the advisor permission. Returns true when the viewer
 * is restricted to read-only access (compliance without advisor). Mirrors the
 * backend write guard so the UI matches what the server will allow.
 */
export function isFlowInstanceReadOnlyForUser(userPermissions: string[]): boolean {
  return (
    userPermissions.includes(PERMISSIONS.COMPLIANCE) &&
    !userPermissions.includes(PERMISSIONS.ADVISOR)
  );
}
